import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { 
  FiTarget, 
  FiTrendingUp, 
  FiCalendar,
  FiDollarSign,
  FiExternalLink,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiSend,
  FiAward,
  FiRefreshCw,
  FiSearch
} from 'react-icons/fi'
import { RiFilePaperLine, RiRadarLine } from 'react-icons/ri'
import './Opportunities.css'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3001/api'

const OpportunityCard = ({ opp, onStatusChange }) => {
  const statusConfig = {
    new: { label: 'New', color: 'accent', icon: <RiRadarLine /> },
    reviewing: { label: 'Reviewing', color: 'warning', icon: <FiClock /> },
    qualified: { label: 'Qualified', color: 'success', icon: <FiCheckCircle /> },
    declined: { label: 'Declined', color: 'danger', icon: <FiXCircle /> },
    submitted: { label: 'Submitted', color: 'info', icon: <FiSend /> },
    won: { label: 'Won!', color: 'success', icon: <FiAward /> },
    lost: { label: 'Lost', color: 'danger', icon: <FiXCircle /> }
  }

  const status = statusConfig[opp.status] || statusConfig.new
  const value = opp.value_estimate 
    ? `${opp.currency || '€'}${parseFloat(opp.value_estimate).toLocaleString()}`
    : 'N/A'

  const deadline = opp.deadline 
    ? new Date(opp.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'No deadline'

  const daysUntilDeadline = opp.deadline
    ? Math.ceil((new Date(opp.deadline) - new Date()) / (1000 * 60 * 60 * 24))
    : null

  return (
    <div className="opportunity-card">
      <div className="opp-header">
        <div className="opp-title-row">
          <h3 className="opp-title">{opp.title}</h3>
          <span className={`opp-status status-${status.color}`}>
            {status.icon}
            {status.label}
          </span>
        </div>
        <div className="opp-meta">
          {opp.source && (
            <span className="opp-source">
              <FiExternalLink size={12} />
              {opp.source}
            </span>
          )}
          {opp.category && (
            <span className="opp-category">{opp.category}</span>
          )}
        </div>
      </div>

      <div className="opp-body">
        {opp.description && (
          <p className="opp-description">{opp.description}</p>
        )}
        <div className="opp-details">
          <div className="opp-detail">
            <FiDollarSign className="detail-icon" />
            <div>
              <div className="detail-label">Value</div>
              <div className="detail-value">{value}</div>
            </div>
          </div>
          <div className="opp-detail">
            <FiCalendar className="detail-icon" />
            <div>
              <div className="detail-label">Deadline</div>
              <div className={`detail-value ${daysUntilDeadline !== null && daysUntilDeadline <= 7 ? 'urgent' : ''}`}>
                {deadline}
                {daysUntilDeadline !== null && daysUntilDeadline <= 7 && (
                  <span className="urgent-badge">{daysUntilDeadline}d left</span>
                )}
              </div>
            </div>
          </div>
          <div className="opp-detail">
            <FiTarget className="detail-icon" />
            <div>
              <div className="detail-label">Match Score</div>
              <div className="detail-value">
                <div className="match-bar-container">
                  <div 
                    className="match-bar" 
                    style={{ 
                      width: `${opp.match_score || 0}%`,
                      background: (opp.match_score || 0) > 70 ? 'var(--green)' : (opp.match_score || 0) > 40 ? 'var(--orange)' : 'var(--red)'
                    }}
                  />
                </div>
                {opp.match_score || 0}%
              </div>
            </div>
          </div>
          {opp.location && (
            <div className="opp-detail">
              <RiRadarLine className="detail-icon" />
              <div>
                <div className="detail-label">Location</div>
                <div className="detail-value">{opp.location}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="opp-footer">
        <select 
          className="status-select"
          value={opp.status}
          onChange={(e) => onStatusChange(opp.id, e.target.value)}
        >
          <option value="new">📡 New</option>
          <option value="reviewing">🔍 Reviewing</option>
          <option value="qualified">✅ Qualified</option>
          <option value="declined">❌ Declined</option>
          <option value="submitted">📤 Submitted</option>
          <option value="won">🏆 Won</option>
          <option value="lost">💔 Lost</option>
        </select>
        {opp.assigned_agent && (
          <span className="opp-agent">via {opp.assigned_agent}</span>
        )}
      </div>
    </div>
  )
}

const Opportunities = () => {
  const [filter, setFilter] = useState('all')

  const { 
    data: oppsData, 
    isLoading: oppsLoading, 
    error: oppsError,
    refetch: refetchOpps 
  } = useQuery('opportunities', async () => {
    const token = localStorage.getItem('token')
    const res = await fetch(`${API_BASE}/opportunities`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!res.ok) throw new Error('Failed to fetch')
    return res.json()
  }, { refetchInterval: 60000 })

  const { data: statsData, refetch: refetchStats } = useQuery('opportunity-stats', async () => {
    const token = localStorage.getItem('token')
    const res = await fetch(`${API_BASE}/opportunities/stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!res.ok) throw new Error('Failed to fetch')
    return res.json()
  }, { refetchInterval: 60000 })

  const handleStatusChange = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('token')
      await fetch(`${API_BASE}/opportunities/${id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })
      refetchOpps()
      refetchStats()
    } catch (err) {
      console.error('Failed to update:', err)
    }
  }

  const opportunities = oppsData?.opportunities || []
  const stats = statsData || {}

  const filtered = filter === 'all' 
    ? opportunities 
    : opportunities.filter(o => o.status === filter)

  const filterTabs = [
    { key: 'all', label: 'All', count: stats.total || opportunities.length },
    { key: 'new', label: 'New', count: stats.new_count },
    { key: 'qualified', label: 'Qualified', count: stats.qualified },
    { key: 'high', label: 'High Value', count: stats.high_value },
  ]

  return (
    <div className="opportunities-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Tenders & Opportunities</h1>
          <p className="page-subtitle">
            AI-discovered opportunities across government and private procurement platforms.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => { refetchOpps(); refetchStats(); }}>
            <FiRefreshCw /> Refresh
          </button>
          <button className="btn btn-primary">
            <FiSearch /> Scan Now
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="opp-stats-row">
        <div className="opp-stat-card">
          <div className="opp-stat-icon" style={{ background: 'rgba(99, 102, 241, 0.15)' }}>
            <RiRadarLine />
          </div>
          <div className="opp-stat-content">
            <div className="opp-stat-value">{stats.total || 0}</div>
            <div className="opp-stat-label">Total Opportunities</div>
          </div>
        </div>
        <div className="opp-stat-card">
          <div className="opp-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.15)' }}>
            <FiClock />
          </div>
          <div className="opp-stat-content">
            <div className="opp-stat-value">{stats.new_count || 0}</div>
            <div className="opp-stat-label">New (Unreviewed)</div>
          </div>
        </div>
        <div className="opp-stat-card">
          <div className="opp-stat-icon" style={{ background: 'rgba(34, 197, 94, 0.15)' }}>
            <FiCheckCircle />
          </div>
          <div className="opp-stat-content">
            <div className="opp-stat-value">{stats.qualified || 0}</div>
            <div className="opp-stat-label">Qualified</div>
          </div>
        </div>
        <div className="opp-stat-card">
          <div className="opp-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.15)' }}>
            <FiCalendar />
          </div>
          <div className="opp-stat-content">
            <div className="opp-stat-value">{stats.upcoming_deadlines || 0}</div>
            <div className="opp-stat-label">Due This Week</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="opp-filters">
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            className={`filter-btn ${filter === tab.key ? 'active' : ''}`}
            onClick={() => setFilter(tab.key)}
          >
            {tab.label}
            {tab.count > 0 && <span className="filter-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* List */}
      {oppsLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Scanning tender boards...</p>
        </div>
      ) : oppsError ? (
        <div className="error-state">
          <FiXCircle size={48} />
          <h3>Failed to load opportunities</h3>
          <p>{oppsError.message}</p>
          <button className="btn btn-secondary" onClick={() => refetchOpps()}>
            <FiRefreshCw /> Try Again
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <RiRadarLine size={48} style={{ opacity: 0.3 }} />
          <h3>No opportunities yet</h3>
          <p>Your tender scout agents are scanning procurement platforms. Results will appear here automatically.</p>
        </div>
      ) : (
        <div className="opportunities-list">
          {filtered.map(opp => (
            <OpportunityCard 
              key={opp.id} 
              opp={opp} 
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Opportunities